import { Badge, Display, Eyebrow, Mono, Prose, Section } from "@ffn/components"
import { createFileRoute, Link, notFound } from "@tanstack/react-router"
import { loadFieldNote, noteEyebrow, readingTime } from "../content/field-notes.js"
import { NoteContents, RelatedNotes } from "../features/NoteNavigation.js"
import { noteHead } from "../seo/index.js"
import "../article.css"
import "../notes.css"

/**
 * Reader: the slug is the path param, the loader resolves it against the
 * compiled-in index and pulls the body chunk on demand, and an unknown slug is a
 * typed not-found rendered by the router's defaultNotFoundComponent. The head
 * (title, description, canonical, Article + BreadcrumbList JSON-LD) derives from
 * the loaded note, so the prerendered HTML carries it too.
 */
export const Route = createFileRoute("/notes/$slug")({
  loader: async ({ params }) => {
    const note = await loadFieldNote(params.slug)
    if (note === undefined) throw notFound()
    return { note }
  },
  head: ({ loaderData }) => (loaderData === undefined ? {} : noteHead(loaderData.note)),
  component: NotePage
})

function NotePage() {
  const { note } = Route.useLoaderData()
  return (
    <Section className="SiteSection">
      <article className="SiteArticle SiteNote" data-kind={note.kind}>
        <header className="SiteArticleHeader">
          <Eyebrow>{noteEyebrow(note)}</Eyebrow>
          <Display>{note.title}</Display>
          <div className="SiteMeta">
            <Mono>
              <time dateTime={note.dateSort}>{note.date}</time>
            </Mono>
            <Mono>
              by <Link to="/about">Laith Al-Saadoon</Link>
            </Mono>
            <Mono>{readingTime(note)}</Mono>
          </div>
          {note.modified === undefined ? null : (
            <p className="SiteNoteUpdated">
              Updated{" "}
              <time dateTime={note.modified}>
                {new Date(`${note.modified}T12:00:00Z`).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  timeZone: "UTC"
                })}
              </time>
            </p>
          )}
          <ul className="SiteTags" aria-label="Topics">
            {note.tags.map((tag) => (
              <li key={tag}>
                <Badge tone="neutral">{tag}</Badge>
              </li>
            ))}
          </ul>
          <div className="SiteArticleLinks">
            <a href={`/notes/${note.slug}.md`} type="text/markdown">
              Read as Markdown
            </a>
            {note.projectUrl === undefined ? null : <a href={note.projectUrl}>Project on GitHub</a>}
          </div>
        </header>
        <div className="SiteReader">
          <NoteContents note={note} />
          <Prose>
            {/*
              The ONE sanctioned dangerouslySetInnerHTML in this app: bodyHtml is
              trusted BUILD-TIME content, generated by scripts/build-field-notes.mjs
              from the wiki pages, with hero images and off-wiki embeds stripped, figures kept, and compiled
              verbatim into the bundle (src/content/field-notes.data.ts). No user
              input ever reaches this sink, and it renders inside <Prose>, whose
              scoped styles it was authored for. The rule id below is matched
              case-sensitively against .semgrep/frontend.yml.
            */}
            {/* nosemgrep: ffn-no-dangerously-set-inner-html */}
            <div dangerouslySetInnerHTML={{ __html: note.bodyHtml }} />
          </Prose>
        </div>
        <RelatedNotes note={note} />
        <p className="SiteNoteBack">
          <Link to="/notes">All Frontier Field Notes</Link>
        </p>
      </article>
    </Section>
  )
}
